import { FC, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import ConnexionLayout from "../../components/ConnexionLayout";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { resetPassword } from "../../services/api/auth";

import "./style.css";

const ResetPassword: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const query = new URLSearchParams(location.search);
    const token = query.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>(
        {}
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const API_URL = process.env.REACT_APP_JT_API;

    useEffect(() => {
        if (token) {
            fetch(`${API_URL}/v2/auth/verify-reset-token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ resetToken: token }),
            })
                .then((response) => {
                    if (response.ok) {
                        setIsTokenValid(true);
                    } else {
                        return response.json().then((data) => {
                            throw new Error(data.message || "Token invalide.");
                        });
                    }
                })
                .catch(() => {
                    setIsTokenValid(false);
                    setErrorMessage("Le token a expiré ou est invalide.");
                });
        } else {
            navigate("/forgot-password");
        }
    }, [token, API_URL, navigate]);

    const handleResetPassword = async () => {
        if (newPassword !== confirmPassword) {
            setFieldErrors({
                confirmPassword: "Les mots de passe ne correspondent pas.",
            });
            return;
        }

        setIsSubmitting(true);
        setFieldErrors({});
        setErrorMessage("");

        try {
            await resetPassword(token!, newPassword, confirmPassword);
            setSuccess(true);
        } catch (error: any) {
            if (error.errors && Array.isArray(error.errors)) {
                const errors = error.errors.reduce((acc: any, curr: any) => {
                    acc[curr.field] = curr.message;
                    return acc;
                }, {});
                setFieldErrors(errors);
            } else {
                setErrorMessage(error.message || "Une erreur s'est produite.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isTokenValid === null) {
        return <p>Chargement...</p>; // Loader pendant la vérification
    }

    if (!isTokenValid) {
        return (
            <ConnexionLayout>
                <p className="connexion_card_title resetpassword_card_title">
                    {errorMessage}
                </p>
                <Button
                    className="resetpassword_button"
                    label="Demander un nouveau lien"
                    variant="secondary"
                    onClick={() => navigate("/forgot-password")}
                />
            </ConnexionLayout>
        );
    }

    return (
        <ConnexionLayout>
            {success ? (
                <>
                    <p className="connexion_card_title">
                        Mot de passe modifié !
                    </p>
                    <p className="connexion_card_subtitle">
                        Votre mot de passe a été modifié avec succès. Vous
                        pouvez maintenant vous connecter.
                    </p>
                    <Button
                        label="Aller à la page de connexion"
                        onClick={() => navigate("/login")}
                    />
                </>
            ) : (
                <>
                    <p className="resetpassword_card_title">
                        Création mot de passe
                    </p>
                    <p className="connexion_card_subtitle">
                        Au moins 8 caractères, avec majuscule, minuscule,
                        chiffre, et un caractère spécial parmi :{" "}
                        {"@ $ ! % * ? & # _ + = . : ; , < > ( ) [ ] { } | ^ ~ -"}
                    </p>
                    <div className="resetpassword_form_inputs">
                        <TextInput
                            className="resetpassword_input"
                            label="Nouveau mot de passe"
                            value={newPassword}
                            setValue={setNewPassword}
                            type="password"
                            error={fieldErrors.newPassword}
                        />

                        <TextInput
                            className="resetpassword_input"
                            label="Confirmer le nouveau mot de passe"
                            value={confirmPassword}
                            setValue={setConfirmPassword}
                            type="password"
                            error={fieldErrors.confirmPassword}
                        />
                    </div>
                    {isSubmitting ? (
                        <CircularProgress className="reset_loader" size={45} />
                    ) : (
                        <Button
                            className="resetpassword_button"
                            label="Enregistrer"
                            variant="secondary"
                            onClick={handleResetPassword}
                        />
                    )}
                </>
            )}
        </ConnexionLayout>
    );
};

export default ResetPassword;
